import React from 'react';
import PropTypes from 'prop-types';
import { createFragmentContainer, graphql } from 'react-relay';

// components
import { ShippingDetailsQuotesTableRow } from './ShippingDetailsQuotesTableRow';
import { shippingModal as messages } from '../shippingMessages';

// helpers
import get from 'lodash.get';

// styles
import styles from './ShippingDetailsQuotes.scss';

const REGION_BOUNDARIES = [
    'DOMESTIC',
    'REGIONAL',
    'DISTRICT',
    'METROPOLITAN',
    'GLOBAL',
    'REST_OF_WORLD',
];

export function ShippingDetailsQuotesTableComponent(props) {
    const { currency, shipmentQuotes, relevantShippingInfo } = props;
    const shipmentQuoteEdges = get(shipmentQuotes, 'edges') || [];
    let hasCustomShipmentQuote = false;

    const regionGroupRows = []; // array of { regionCode, regionName, regionBoundary }
    const groupedShipmentQuotes = {}; // shipment quotes grouped by regionCode
    //handle custom quote
    if (get(relevantShippingInfo, 'shipmentQuote.isCustom')) {
        hasCustomShipmentQuote = true;
    }

    shipmentQuoteEdges.forEach(({ node: quote }) => {
        const regionCode = get(quote, 'serviceMethod.region.code');
        const regionName = get(quote, 'serviceMethod.region.regionName');
        const regionBoundary = get(quote, 'serviceMethod.region.regionBoundary.type.name');

        if (!groupedShipmentQuotes[regionCode]) {
            groupedShipmentQuotes[regionCode] = [];
            regionGroupRows.push({ regionCode, regionName, regionBoundary });
        }

        // add quote to group
        groupedShipmentQuotes[regionCode].push(quote);
    }, {});

    if (!regionGroupRows.length && !hasCustomShipmentQuote) {
        return null;
    }

    // sort array by regionBoundary then by regionName
    regionGroupRows.sort((a, b) => {
        const boundarySort =
            REGION_BOUNDARIES.indexOf(a.regionBoundary) -
            REGION_BOUNDARIES.indexOf(b.regionBoundary);

        if (boundarySort) {
            return boundarySort;
        }
        if (a.regionName < b.regionName) {
            return -1;
        }
        if (a.regionName > b.regionName) {
            return 1;
        }
        return 0;
    });

    return (
        <div>
            <table className={styles.quotesTable}>
                <thead className={styles.quotesTableHeaderRow}>
                    <tr>
                        <th data-tn="ship-destination" className={styles.quotesTableHeaderCell}>
                            {messages.shipTo}
                        </th>
                        <th data-tn="ship-method-value" className={styles.quotesTableHeaderCell}>
                            {messages.method}
                        </th>
                        <th data-tn="ship-time-value" className={styles.quotesTableHeaderCell}>
                            {messages.time}
                        </th>
                        <th data-tn="ship-price-value" className={styles.quotesTableHeaderCell}>
                            {messages.price}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {hasCustomShipmentQuote && (
                        <ShippingDetailsQuotesTableRow
                            key={null}
                            currency={currency}
                            shipmentQuotes={null}
                            relevantShippingInfo={relevantShippingInfo}
                        />
                    )}
                    {!!regionGroupRows.length &&
                        regionGroupRows.map(({ regionCode }) => (
                            <ShippingDetailsQuotesTableRow
                                key={regionCode}
                                currency={currency}
                                shipmentQuotes={groupedShipmentQuotes[regionCode]}
                                relevantShippingInfo={relevantShippingInfo}
                            />
                        ))}
                </tbody>
            </table>
        </div>
    );
}

ShippingDetailsQuotesTableComponent.propTypes = {
    currency: PropTypes.string,
    shipmentQuotes: PropTypes.object.isRequired,
    relevantShippingInfo: PropTypes.object.isRequired,
};
export const ShippingDetailsQuotesTable = createFragmentContainer(
    ShippingDetailsQuotesTableComponent,
    {
        shipmentQuotes: graphql`
            fragment ShippingDetailsQuotesTable_shipmentQuotes on ShipmentQuotesConnection {
                edges {
                    node {
                        ...ShippingDetailsQuotesTableRow_shipmentQuotes
                        serviceMethod {
                            region {
                                code
                                regionName
                                regionBoundary {
                                    type {
                                        name
                                    }
                                }
                            }
                        }
                    }
                }
            }
        `,
        relevantShippingInfo: graphql`
            fragment ShippingDetailsQuotesTable_relevantShippingInfo on QueryRelevantShippingInfo {
                ...ShippingDetailsQuotesTableRow_relevantShippingInfo
                shipmentQuote {
                    isCustom
                    serviceMethod {
                        region {
                            code
                            regionName
                            regionBoundary {
                                type {
                                    name
                                }
                            }
                        }
                    }
                }
            }
        `,
    }
);
